import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiMinus, FiCheck } = FiIcons;

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqData = [
    {
      question: "Is this just expensive sleep hygiene advice?",
      answer: "Nope. We're training the actual brain functions that control sleep. Think personal training for your prefrontal cortex."
    },
    {
      question: "Is this a replacement for seeing a doctor?",
      answer: "Not at all. We complement medical care",
      details: [
        "SEE A DOCTOR FIRST for diagnosing and treating medical conditions like sleep apnea, insomnia, or restless leg syndrome.",
        "THEN WORK WITH US to build the cognitive skills, routines, and behaviors that lead to lasting change and optimal daily performance."
      ]
    },
    {
      question: "20% improvement sounds small—worth it?",
      answer: "A 20% performance boost is the difference between constant lag and seamless multitasking. It's not a small change; it's a life-changing one. Also, every small gain stacks up to big change. We keep it real, no overpromises—just steady progress."
    },
    {
      question: "Why would I work with a speech language pathologist to address sleep?",
      answer: "We're willing to bet that your late night thoughts and your daytime distractions consist of some kind of inner chatter. SLPs specialize in how language shapes thought, and can help you with your speaking voice OR your inner voice. Clearer language helps clearer thinking—and that improves your executive functions, making sleep possible."
    },
    {
      question: "Do I have to give up my current sleep routine?",
      answer: "Keep what works. We'll identify what's sabotaging you and build the mental skills to make everything more effective."
    },
    {
      question: "How is this different from meditation or mindfulness?",
      answer: "We target specific executive functions with evidence-based cognitive training. This involves many exercises that are tailored to you. More precise than general mindfulness, more powerful than counting breaths."
    },
    {
      question: "What if I've tried therapy before?",
      answer: "Traditional therapy focuses on behaviors. We focus on the brain functions that drive those behaviors."
    },
    {
      question: "Will I become dependent on this like sleeping pills?",
      answer: "The opposite. You're building skills that become stronger over time. No pills, no apps, no crutches—just a better-trained brain."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
        </motion.div>

        <div className="space-y-4">
          {faqData.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full text-left p-6 focus:outline-none focus:ring-2 focus:ring-accent-magenta focus:ring-inset"
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-playfair font-bold text-gray-900 pr-4">
                    {faq.question}
                  </h3>
                  <div className="flex-shrink-0">
                    <SafeIcon
                      icon={openIndex === index ? FiMinus : FiPlus}
                      className="w-6 h-6 text-accent-magenta transition-transform duration-200"
                    />
                  </div>
                </div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-gray-700 font-raleway leading-relaxed mb-4">
                          {faq.answer}
                        </p>
                        
                        {faq.details && (
                          <div className="space-y-3">
                            {faq.details.map((detail, detailIndex) => (
                              <div key={detailIndex} className="flex items-start gap-3">
                                <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                                <p className="text-gray-700 font-raleway leading-relaxed">
                                  {detail}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;